import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import 'react-toastify/dist/ReactToastify.css';
import PublicFooter from '../components/layout/PublicFooter';
import Header from './component/Header';
import { API_URL } from '../config';
import Swal from 'sweetalert2';
import ContactModal from './component/ContactModal';
import { useAuth } from './component/AuthContext';
import { Link } from 'react-router-dom';

const Profile = () => {
    const [userProfile, setUserProfile] = useState({
        name: '', email: '', password: '', confirmPassword: '',
    });
    const [savedContacts, setSavedContacts] = useState([]);
    const [activeTab, setActiveTab] = useState('profile');
    const { updateProfile } = useAuth();

    const handleSaveClick = (e) => {
        e.preventDefault();
        const validationErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!userProfile.email) validationErrors.email = 'Please enter an email.';
        else if (!emailRegex.test(userProfile.email)) validationErrors.email = 'Please enter a valid email.';
        if (!userProfile.password) validationErrors.password = 'Please enter a new password.';
        if (!userProfile.confirmPassword) validationErrors.confirmPassword = 'Please confirm your new password.';
        if (userProfile.password !== userProfile.confirmPassword) validationErrors.confirmPassword = 'Passwords do not match.';

        if (Object.keys(validationErrors).length > 0) {
            Swal.fire({ icon: 'error', title: 'Please fix the following issues:', html: Object.values(validationErrors).join('<br>'), showCloseButton: true });
            return;
        }

        const token = localStorage.getItem('token');
        fetch(API_URL + '/update-profile', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(userProfile),
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json().then((data) => {
                Swal.fire({ icon: 'success', title: 'Profile updated successfully!', timer: 2000 });
                updateProfile(data);
              });
            } else if (response.status === 422) {
              response.json().then((data) => {
                const errorMessages = data.errors ? Object.values(data.errors).join('<br>') : data.message || 'An error occurred.';
                Swal.fire({ icon: 'error', title: 'Profile update failed:', html: errorMessages, showCloseButton: true });
              });
            }
          })
          .catch((error) => console.error('Error updating profile:', error));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserProfile({ ...userProfile, [name]: value });
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            const user = JSON.parse(localStorage.getItem('user'));
            if (user) setUserProfile({ name: user.name, email: user.email, password: '', confirmPassword: '' });
        }
    }, []);

    const fetchSavedContacts = () => {
        const token = localStorage.getItem('token');
        fetch(`${API_URL}/customer/notification/methods`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` },
        })
          .then((response) => { if (!response.ok) throw new Error('Network response was not ok'); return response.json(); })
          .then((data) => setSavedContacts(data))
          .catch((error) => console.error('Error fetching saved contacts:', error));
    };

    useEffect(() => { fetchSavedContacts(); }, []);

    const deleteContact = (contactId) => {
        const token = localStorage.getItem('token');
        fetch(`${API_URL}/customer/notification/methods/${contactId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
        })
          .then((response) => { if (!response.ok) return response.json().then((data) => { throw new Error(data.error || 'Network response was not ok'); }); return response.json(); })
          .then(() => {
            setSavedContacts(savedContacts.filter((contact) => contact.id !== contactId));
            Swal.fire('Deleted!', 'The contact has been deleted.', 'success');
          })
          .catch((error) => { Swal.fire('Error', error.message, 'error'); });
    };

    const handleDeleteClick = (contactId) => {
        Swal.fire({
            title: 'Are you sure?', text: 'You will not be able to recover this contact!', icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Yes, delete it!',
        }).then((result) => { if (result.isConfirmed) deleteContact(contactId); });
    };

    const [selectedContact, setSelectedContact] = useState(null);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);

    const openContactModal = (contact) => { setSelectedContact(contact); setIsContactModalOpen(true); };
    const closeContactModal = () => { setSelectedContact(null); setIsContactModalOpen(false); };
    const handleContactSave = () => { closeContactModal(); fetchSavedContacts(); };

    const inputClasses = "tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-py-2.5 tw-text-sm tw-text-gray-900 tw-outline-none focus:tw-border-blue-500 focus:tw-ring-2 focus:tw-ring-blue-500/20 tw-transition-all";

    return (
        <div className="tw-min-h-screen tw-flex tw-flex-col tw-bg-gray-50">
            <Header />

            <div className="tw-flex-1 tw-max-w-4xl tw-mx-auto tw-w-full tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-8">
                {/* Page header */}
                <div className="tw-mb-8">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500 tw-mb-2">
                        <Link to="/account" className="tw-text-blue-600 hover:tw-text-blue-700 tw-no-underline">Dashboard</Link>
                        <svg className="tw-w-4 tw-h-4 tw-text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        <span>Profile</span>
                    </div>
                    <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-m-0">Your Profile</h1>
                </div>

                {/* Tabs */}
                <div className="tw-flex tw-gap-1 tw-bg-gray-200 tw-rounded-lg tw-p-1 tw-mb-6 tw-w-fit">
                    <button
                        onClick={() => setActiveTab('profile')}
                        className={`tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md tw-border-0 tw-cursor-pointer tw-transition-all ${
                            activeTab === 'profile' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-bg-transparent tw-text-gray-600 hover:tw-text-gray-900'
                        }`}
                    >
                        Main Profile
                    </button>
                    <button
                        onClick={() => setActiveTab('contacts')}
                        className={`tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md tw-border-0 tw-cursor-pointer tw-transition-all ${
                            activeTab === 'contacts' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-bg-transparent tw-text-gray-600 hover:tw-text-gray-900'
                        }`}
                    >
                        Saved Contacts
                    </button>
                </div>

                {/* Main Profile Tab */}
                {activeTab === 'profile' && (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-p-6">
                        <div className="tw-max-w-md tw-space-y-4">
                            <div className="tw-flex tw-flex-col tw-gap-1.5">
                                <label className="tw-text-sm tw-font-medium tw-text-gray-700">Name</label>
                                <input type="text" name="name" value={userProfile.name} onChange={handleInputChange} className={inputClasses} />
                            </div>
                            <div className="tw-flex tw-flex-col tw-gap-1.5">
                                <label className="tw-text-sm tw-font-medium tw-text-gray-700">Email</label>
                                <input type="email" name="email" value={userProfile.email} onChange={handleInputChange} className={`${inputClasses} tw-bg-gray-50 tw-text-gray-500`} readOnly />
                            </div>
                            <div className="tw-flex tw-flex-col tw-gap-1.5">
                                <label className="tw-text-sm tw-font-medium tw-text-gray-700">New Password</label>
                                <input type="password" name="password" value={userProfile.password} onChange={handleInputChange} className={inputClasses} placeholder="Enter new password" />
                            </div>
                            <div className="tw-flex tw-flex-col tw-gap-1.5">
                                <label className="tw-text-sm tw-font-medium tw-text-gray-700">Confirm Password</label>
                                <input type="password" name="confirmPassword" value={userProfile.confirmPassword} onChange={handleInputChange} className={inputClasses} placeholder="Confirm new password" />
                            </div>
                            <div className="tw-pt-2">
                                <button onClick={handleSaveClick} className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-text-sm tw-font-medium tw-px-6 tw-py-2.5 tw-rounded-lg tw-border-0 tw-cursor-pointer tw-transition-colors">
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Saved Contacts Tab */}
                {activeTab === 'contacts' && (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm">
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-gray-200">
                            <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">Saved Contacts</h2>
                            <button
                                onClick={() => openContactModal(null)}
                                className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-text-sm tw-font-medium tw-px-4 tw-py-2 tw-rounded-lg tw-border-0 tw-cursor-pointer tw-transition-colors"
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                Add Contact
                            </button>
                        </div>

                        {savedContacts.length > 0 ? (
                            <div className="tw-overflow-x-auto">
                                <table className="tw-w-full">
                                    <thead>
                                        <tr className="tw-border-b tw-border-gray-200 tw-bg-gray-50">
                                            <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Phone</th>
                                            <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Email</th>
                                            <th className="tw-text-left tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Address</th>
                                            <th className="tw-text-right tw-px-6 tw-py-3 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="tw-divide-y tw-divide-gray-100">
                                        {savedContacts.map((contact) => (
                                            <tr key={contact.id} className="hover:tw-bg-gray-50 tw-transition-colors">
                                                <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">{contact.phone}</td>
                                                <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">{contact.email}</td>
                                                <td className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-700">{contact.address}</td>
                                                <td className="tw-px-6 tw-py-4 tw-text-right">
                                                    <button onClick={() => openContactModal(contact)} className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-700 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-mr-3">
                                                        <FontAwesomeIcon icon={faEdit} className="tw-mr-1" /> Edit
                                                    </button>
                                                    <button onClick={() => handleDeleteClick(contact.id)} className="tw-text-sm tw-text-red-600 hover:tw-text-red-700 tw-border-0 tw-bg-transparent tw-cursor-pointer">
                                                        <FontAwesomeIcon icon={faTrashAlt} className="tw-mr-1" /> Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="tw-px-6 tw-py-12 tw-text-center">
                                <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                                    <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center">
                                        <svg className="tw-w-6 tw-h-6 tw-text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128H5.228A2.228 2.228 0 013 16.9c0-2.09 1.666-4.535 5.03-4.535.74 0 1.426.107 2.04.296M12 12.75a3.75 3.75 0 110-7.5 3.75 3.75 0 010 7.5z" /></svg>
                                    </div>
                                    <p className="tw-text-sm tw-text-gray-500 tw-m-0">No contacts saved yet</p>
                                    <button onClick={() => openContactModal(null)} className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-700 tw-font-medium tw-border-0 tw-bg-transparent tw-cursor-pointer">
                                        Add your first contact
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <PublicFooter />
            <ContactModal isOpen={isContactModalOpen} onClose={closeContactModal} contact={selectedContact} onSave={handleContactSave} />
        </div>
    );
};

export default Profile;
